import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { useToast } from "../../hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Key, Plus, Trash2, Copy, AlertTriangle } from "lucide-react";
import api from "../../services/api";

const apiKeyService = {
  list: () => api.get('/admin/api-keys/'),
  create: (label) => api.post('/admin/api-keys/', { label }),
  revoke: (id) => api.delete(`/admin/api-keys/${id}`),
};

export default function ApiKeysTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newLabel, setNewLabel] = useState("");
  const [createdKey, setCreatedKey] = useState(null); // { key, label } shown once

  const { data, isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: () => apiKeyService.list(),
  });
  const keys = data?.data?.keys || [];

  const createMutation = useMutation({
    mutationFn: (label) => apiKeyService.create(label),
    onSuccess: (resp) => {
      const result = resp?.data;
      if (result?.key) setCreatedKey({ key: result.key, label: result.label });
      setNewLabel("");
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast({ description: "API key created. Copy it now — it won't be shown again." });
    },
    onError: (err) => toast({ variant: "destructive", description: err?.detail || "Failed to create key" }),
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => apiKeyService.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast({ description: "Key revoked. Any external service using it will stop working." });
    },
    onError: (err) => toast({ variant: "destructive", description: err?.detail || "Failed to revoke" }),
  });

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast({ description: "Copied to clipboard" });
  };

  const exampleEndpoint = `${window.location.origin}/api/public/leads`;
  const exampleCurl = `curl -X POST ${exampleEndpoint} \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_KEY_HERE" \\
  -d '{
    "name": "Jane",
    "last_name": "Doe",
    "email": "jane@example.com",
    "phone": "555-123-4567",
    "service_type": "Landscape Design",
    "notes": "Looking for a quote",
    "source": "Website Contact Form"
  }'`;

  return (
    <div className="space-y-6">
      {/* Just-created key (shown ONCE) */}
      {createdKey && (
        <Card className="border-green-300 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-900">
              <Key className="h-5 w-5" /> New API Key Created
            </CardTitle>
            <CardDescription className="text-green-800">
              Copy this key NOW. For security it cannot be retrieved again — if lost, revoke it and create a new one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Label</Label>
              <div className="font-medium">{createdKey.label}</div>
            </div>
            <div>
              <Label className="text-xs">API Key</Label>
              <div className="flex gap-2">
                <Input value={createdKey.key} readOnly className="font-mono text-sm" />
                <Button onClick={() => handleCopy(createdKey.key)}><Copy className="h-4 w-4 mr-1" />Copy</Button>
              </div>
            </div>
            <Button variant="outline" onClick={() => setCreatedKey(null)}>I've saved the key</Button>
          </CardContent>
        </Card>
      )}

      {/* Create new key */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Key className="h-5 w-5" /> Public API Keys</CardTitle>
          <CardDescription>
            Used by external systems (your website contact form, Zapier, etc.) to push leads INTO the CRM.
            Each key should have a descriptive label so you know what's using it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">New key label</Label>
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Website Contact Form, Zapier, Bark.com"
                className="h-9"
              />
            </div>
            <Button
              onClick={() => createMutation.mutate(newLabel)}
              disabled={!newLabel.trim() || createMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700"
            >
              <Plus className="h-4 w-4 mr-1" />Create Key
            </Button>
          </div>

          {/* Existing keys */}
          {isLoading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : keys.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border rounded-lg">
              <Key className="h-12 w-12 mx-auto mb-2 opacity-30" />
              No API keys yet. Create one above to enable external systems to submit leads.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Label</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Key (preview)</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Uses</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Last Used</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {keys.map((k) => (
                    <tr key={k.id} className={k.is_active ? "" : "opacity-50"}>
                      <td className="px-3 py-2 font-medium">{k.label}</td>
                      <td className="px-3 py-2 font-mono text-xs">{k.key_preview}</td>
                      <td className="px-3 py-2">
                        {k.is_active ? <Badge className="bg-green-100 text-green-800">Active</Badge> : <Badge variant="outline">Revoked</Badge>}
                      </td>
                      <td className="px-3 py-2 text-right">{k.usage_count || 0}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "Never"}</td>
                      <td className="px-3 py-2 text-right">
                        {k.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (window.confirm(`Revoke "${k.label}"? Any external service using this key will stop working immediately.`)) {
                                revokeMutation.mutate(k.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documentation */}
      <Card>
        <CardHeader>
          <CardTitle>How to use the public lead API</CardTitle>
          <CardDescription>Share these details with your web developer to wire up the contact form</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <Label className="text-xs">Endpoint</Label>
            <div className="flex gap-2 mt-1">
              <Input value={exampleEndpoint} readOnly className="font-mono" />
              <Button variant="outline" onClick={() => handleCopy(exampleEndpoint)}><Copy className="h-4 w-4" /></Button>
            </div>
          </div>

          <div>
            <Label className="text-xs">Method</Label>
            <div className="font-mono">POST</div>
          </div>

          <div>
            <Label className="text-xs">Headers</Label>
            <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">{`Content-Type: application/json
X-API-Key: <your_key_here>`}</pre>
          </div>

          <div>
            <Label className="text-xs">Body (JSON)</Label>
            <p className="text-xs text-gray-500 mb-1">Required: <code>name</code>. Everything else is optional.</p>
            <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">{`{
  "name": "Jane",
  "last_name": "Doe",
  "email": "jane@example.com",
  "phone": "555-123-4567",
  "address": "123 Main St",
  "city": "Roseville",
  "zip_code": "95661",
  "service_type": "Landscape Design",
  "notes": "Looking for a quote on backyard renovation",
  "source": "Website Contact Form",
  "budget": "$25,000 - $50,000"
}`}</pre>
          </div>

          <div>
            <Label className="text-xs">Example: cURL</Label>
            <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">{exampleCurl}</pre>
            <Button variant="outline" size="sm" className="mt-1" onClick={() => handleCopy(exampleCurl)}>
              <Copy className="h-4 w-4 mr-1" />Copy
            </Button>
          </div>

          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs flex gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <strong>Security:</strong> Never put the API key directly in client-side JavaScript on a public website —
              anyone could view-source and steal it. Instead, have your website POST to a server-side endpoint
              (PHP/Node/etc.) that holds the key and forwards to this CRM endpoint.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
